import { BaseModal } from "../../../../components/common/BaseModal/BaseModal";
import { useUiTranslator } from "../../../../hooks/useUiTranslator";
import { usePutApiLineItemApiLineItemProperties } from "../../../../openapi/backendApi";
import { useCadStatus } from "../../customContext/CadStatusContext";

import { JSX, useState } from "react";

import { useStatus } from "@4pace-cpq/core";
import {
  VMConfigurations,
  VMLineItems,
  useCreateConfigurations,
  useCreateLineItems,
  useGetAllCompanies,
  useGetByIdConfigurations,
  useGetByIdQuotesKey,
  useUpdateConfigurations,
} from "@4pace-cpq/core/api";
import { Box, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import {
  useConfigurationApi,
  useCsConfigurationApi,
  useRootCfgObject,
  useSessionId,
} from "@plansoft-configuration-renderer/framework";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";

export function SaveConfigurationButton({
  configurationId,
  label,
  navigationCallback,
  fullWidth,
}: {
  configurationId: VMConfigurations["id"];
  label?: string;
  navigationCallback?: () => void;
  fullWidth?: boolean;
}): JSX.Element {
  const { itemGroupId, lineItemId } = useSearch({
    strict: false,
  });
  const queryClient = useQueryClient();
  const { isCadLoading } = useCadStatus();
  const navigate = useNavigate();
  const { isComplete } = useRootCfgObject();
  const { data: companies } = useGetAllCompanies();
  const { mutate: createLineItem } = useCreateLineItems();
  const { mutate: createConfiguration } = useCreateConfigurations();
  /* const { mutate: setLineItemProperties } =
    useLineItemApiServicePutApiLineItemApiLineItemProperties(); */
  const { mutate: setLineItemProperties } =
    usePutApiLineItemApiLineItemProperties();
  const sessionId = useSessionId();
  const api = useConfigurationApi();

  const { status } = useStatus();

  const t = useUiTranslator();
  const { saveConfiguration } = useCsConfigurationApi();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const handleCloseModal = (): void => {
    setIsModalOpen(false);
  };
  const handleOpenModal = (): void => {
    setIsModalOpen(true);
  };

  const { data: configuration } = useGetByIdConfigurations(
    {
      path: { id: configurationId as string },
    },
    undefined,
    { enabled: !!configurationId },
  );

  const { mutate: updateConfiguration } = useUpdateConfigurations();

  const handleUpdateLineItemProperties = async (
    lineItemId: string,
  ): Promise<string> => {
    const res = await api.invokeUserDefinedGetter({
      id: sessionId,
      name: "getProjectListInfo",
      invokeUserDefinedGetterParams: {
        path: "/XAS_PRODUCT_VIEW/0/",
        input: JSON.stringify({}),
      },
    });
    if (res?.output) {
      const data = JSON.parse(res?.output);

      setLineItemProperties({
        params: { lineItemId: lineItemId },
        data: {
          typeCode: data.typeCode,
          /*   priceIndicator: data.priceIndicator
              ? data.priceIndicator.toString()
              : "0", */
        },
      });
      return res?.output;
    }
    return "";
  };

  const handleCreateConfiguration = async (
    lineItemId: VMLineItems["id"],
  ): Promise<void> => {
    const { saveState } = await saveConfiguration();
    if (saveState) {
      const stateJSON = JSON.stringify(saveState);

      createConfiguration(
        {
          body: {
            //todo remove if BE allows to remove
            identifier: "-",
            lineItemId,
            state: stateJSON,
          },
        },
        {
          onSuccess: async ({ data: configuration }) => {
            if (!configuration) return;
            await handleUpdateLineItemProperties(lineItemId);

            queryClient.invalidateQueries({
              queryKey: [useGetByIdQuotesKey],
            });
            if (typeof navigationCallback === "function") {
              navigationCallback(); //This Callback normally navigates to another page.
            } else {
              navigate({
                to: "/configurator",
                replace: true,
                search: (prevSearch: object) => {
                  return {
                    ...prevSearch,
                    configurationId: configuration.id,
                    lineItemId,
                    dummy: "test",
                  };
                },
              });
            }
          },
          onError: (error) => {
            console.error("Error creating configuration", error);
          },
        },
      );
    }
  };

  const handleCreateLineItem = async (): Promise<void> => {
    if (itemGroupId && companies && status) {
      await createLineItem(
        {
          body: {
            companyId: companies[0]?.id || "",
            statusId: status.default.id || "",
            itemGroupId,
          },
        },
        {
          onSuccess: async ({ data: lineItem }) => {
            if (!lineItem) return;
            await handleCreateConfiguration(lineItem.id);
          },
          onError: (error) => {
            console.error("Error creating line item", error);
          },
        },
      );
    }
  };

  async function handleClick(): Promise<void> {
    if (!lineItemId) {
      await handleCreateLineItem();
    } else {
      const { saveState } = await saveConfiguration();

      if (saveState) {
        const stateJSON = JSON.stringify(saveState);
        if (configurationId && configuration && configuration.lineItemId) {
          updateConfiguration(
            {
              path: { id: configurationId },
              body: {
                rowVersion: configuration.rowVersion,
                state: stateJSON,
                lineItemId: configuration.lineItemId,
              },
            },
            {
              onError: (error) => {
                console.error("Error creating configuration", error);
              },
            },
          );

          await handleUpdateLineItemProperties(configuration.lineItemId);
        } else {
          console.error("Error creating configuration", { configurationId });
        }
        if (typeof navigationCallback === "function") {
          navigationCallback(); //navigationCallback normally navigates to another page.
        }
      }
    }
  }

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={handleOpenModal}
        fullWidth={fullWidth}
        disabled={isCadLoading || !isComplete}
      >
        <Typography variant="body1">
          {label
            ? label
            : t(
                "CustomApp:buttons.SaveConfigurationButton",
                "Save Configuration",
              )}
        </Typography>
      </Button>

      <BaseModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={
          label
            ? label
            : t(
                "CustomApp:buttons.SaveConfigurationButton",
                "Save Configuration",
              )
        }
        customSx={{
          minWidth: "30em",
          width: "25%",
          overflow: "auto",
        }}
      >
        <Typography variant="caption">
          {t(
            "CustomApp:modal.SaveConfiguration.note",
            "You are about to save your current configuration and be redirected to the Project List page. Do you want to continue?",
          )}
        </Typography>
        <Box display="flex" gap={2} justifyContent="space-evenly" mt={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleClick}
            disabled={isCadLoading || !isComplete}
            loadingPosition="start"
            loading={isCadLoading}
          >
            {t(
              "CustomApp:buttons.SaveConfigurationButton",
              "Save Configuration",
            )}
          </Button>
        </Box>
      </BaseModal>
    </>
  );
}
